(() => {
  // Close-Up checkout gate: Close-Up Bills require sign-in + verification
  // BEFORE checkout, but must never block cart management. Cart writes are
  // enforced server-side (validation functions skip CART_INTERACTION), so
  // this script only upgrades the checkout attempt itself from a raw
  // platform error into a clean modal. Carts without Close-Ups pass through
  // synchronously with zero added requests or delay.
  const root = document.getElementById("pab-closeup-gate");
  if (!root) return;

  const data = root.dataset;
  let dialog = null;
  let passOnce = false;

  function config() {
    return {
      hasCloseup: data.hasCloseup === "true",
      loggedIn: data.loggedIn === "true",
      approved: data.approved === "true",
      statusUrl: data.statusUrl,
      loginUrl: data.loginUrl,
      manualUrl: data.manualUrl,
    };
  }

  // New customer-accounts login links carry return_to; classic account
  // links carry nothing. Either way the buyer must land back in checkout
  // so they can resume with the same cart.
  function loginContinueUrl(url) {
    if (!url) return "/checkout";
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.searchParams.has("return_to")) parsed.searchParams.set("return_to", "/checkout");
      else parsed.searchParams.set("return_url", "/checkout");
      return parsed.pathname + parsed.search + parsed.hash;
    } catch (error) {
      const separator = url.indexOf("?") === -1 ? "?" : "&";
      return url + separator + "return_url=" + encodeURIComponent("/checkout");
    }
  }

  function isCheckoutTrigger(element) {
    if (!element || typeof element.closest !== "function") return null;
    return element.closest(
      'a[href*="/checkout"], button[name="checkout"], input[name="checkout"]'
    );
  }

  function continueCheckout(trigger) {
    passOnce = true;
    if (trigger.tagName === "A") {
      window.location.assign(trigger.getAttribute("href"));
      window.setTimeout(() => { passOnce = false; }, 1000);
      return;
    }
    if (trigger.tagName === "FORM") {
      trigger.submit();
      window.setTimeout(() => { passOnce = false; }, 1000);
      return;
    }
    trigger.click();
    window.setTimeout(() => { passOnce = false; }, 1000);
  }

  function looksLikeCloseup(handle, title) {
    const h = (handle || "").toLowerCase();
    const t = (title || "").toLowerCase();
    return h.indexOf("close-up") !== -1 || t.indexOf("close-up") !== -1;
  }

  async function productHasCloseupTag(handle) {
    try {
      const response = await fetch("/products/" + handle + ".js", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return false;
      const product = await response.json();
      const tags = product.tags || [];
      return tags.some((tag) => String(tag).toLowerCase() === "close-up");
    } catch (error) {
      return false;
    }
  }

  async function cartHasCloseup() {
    const response = await fetch("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("cart unavailable");
    const cart = await response.json();
    const items = cart.items || [];
    for (const item of items) {
      if (looksLikeCloseup(item.handle, item.product_title)) return true;
    }
    for (const item of items) {
      if (item.handle && (await productHasCloseupTag(item.handle))) return true;
    }
    return false;
  }

  async function fetchStatus(statusUrl) {
    const response = await fetch(statusUrl, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!response.ok && response.status !== 401) throw new Error("status unavailable");
    return response.json();
  }

  function buildDialog() {
    if (dialog) return dialog;
    dialog = document.createElement("dialog");
    dialog.id = "pab-closeup-checkout-dialog";
    dialog.innerHTML =
      '<div class="pab-closeup-gate__panel">' +
        '<button class="pab-closeup-gate__close" type="button" aria-label="Close">×</button>' +
        '<p class="pab-closeup-gate__eyebrow">Close-Up Bills</p>' +
        '<h2 class="pab-closeup-gate__title">One quick step before checkout</h2>' +
        '<p class="pab-closeup-gate__body">Your cart includes Close-Up Bills, which require sign-in and verification before checkout. Your cart is saved — nothing will be removed.</p>' +
        '<a class="pab-closeup-gate__primary" href="#">Sign in &amp; continue</a>' +
        '<button class="pab-closeup-gate__secondary" type="button">Return to cart</button>' +
      "</div>";
    document.body.append(dialog);
    dialog.querySelector(".pab-closeup-gate__close").addEventListener("click", () => dialog.close());
    dialog.querySelector(".pab-closeup-gate__secondary").addEventListener("click", () => dialog.close());
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) dialog.close();
    });
    return dialog;
  }

  function showGate(loggedIn, loginUrl, manualUrl) {
    buildDialog();
    const primary = dialog.querySelector(".pab-closeup-gate__primary");
    if (loggedIn) {
      primary.textContent = "Complete verification";
      primary.setAttribute("href", manualUrl);
    } else {
      primary.textContent = "Sign in & continue";
      primary.setAttribute("href", loginContinueUrl(loginUrl));
    }
    if (typeof dialog.showModal === "function") {
      if (!dialog.open) dialog.showModal();
      primary.focus();
    } else {
      window.location.assign(primary.getAttribute("href"));
    }
  }

  async function handleCheckoutIntent(event, trigger) {
    if (passOnce) {
      passOnce = false;
      return;
    }
    // Modified clicks (new tab/window) and non-left buttons pass through;
    // the platform still enforces verification at checkout.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof event.button === "number" && event.button !== 0) return;

    const cfg = config();
    if (!cfg.hasCloseup || cfg.approved) return;

    event.preventDefault();
    event.stopPropagation();

    try {
      const [freshHasCloseup, status] = await Promise.all([
        cartHasCloseup(),
        fetchStatus(cfg.statusUrl),
      ]);
      const freshLoggedIn = status.loggedIn === true;
      const freshApproved = status.approved === true;
      data.hasCloseup = freshHasCloseup ? "true" : "false";
      data.loggedIn = freshLoggedIn ? "true" : "false";
      data.approved = freshApproved ? "true" : "false";
      if (!freshHasCloseup || freshApproved) {
        continueCheckout(trigger);
        return;
      }
      showGate(freshLoggedIn, cfg.loginUrl, cfg.manualUrl);
    } catch (error) {
      // Fail open: never strand a buyer on a status/cart lookup failure.
      // The platform still enforces Close-Up verification at checkout.
      continueCheckout(trigger);
    }
  }

  document.addEventListener(
    "click",
    (event) => {
      if (event.defaultPrevented) return;
      const trigger = isCheckoutTrigger(event.target);
      if (!trigger || trigger.disabled) return;
      handleCheckoutIntent(event, trigger);
    },
    true
  );

  document.addEventListener("submit", (event) => {
    if (event.defaultPrevented || passOnce) return;
    const form = event.target;
    if (!form || !form.getAttribute) return;
    const action = form.getAttribute("action") || "";
    if (action.indexOf("/checkout") === -1) return;
    const cfg = config();
    if (!cfg.hasCloseup || cfg.approved) return;
    event.preventDefault();
    event.stopPropagation();
    handleCheckoutIntent(event, form);
  }, true);
})();
